import { useState } from "react";
import {
  X,
  FloppyDisk,
  MusicNote,
  UploadSimple,
  Trash,
  ArrowUp,
  ArrowDown,
  CheckCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

function mediaUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

/**
 * MusicManagerDrawer
 *
 * Lets a logged-in musician curate their own release + tracks:
 *  - Upload/replace cover art, set EP/album title, year.
 *  - Add multiple tracks, each with an audio upload (mp3/wav), title,
 *    BPM, musical key, explicit flag, and a featured toggle.
 *  - Reorder / remove tracks.
 *
 * All data is persisted via PATCH /api/users/me { tracks, music_release }.
 */
export default function MusicManagerDrawer({ user, onClose, onSaved }) {
  const initialRelease = user.music_release || {
    title: "",
    type: "EP",
    year: new Date().getFullYear(),
    cover: "",
  };
  const [release, setRelease] = useState(initialRelease);
  const [tracks, setTracks] = useState(user.tracks || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);

  const uploadCover = async (file) => {
    if (!file) return;
    setError("");
    setUploading("cover");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRelease((r) => ({ ...r, cover: data.url }));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  const addTrack = () => {
    setTracks((t) => [
      ...t,
      {
        id: `t_${Date.now()}`,
        title: "Nuevo track",
        audio_url: "",
        duration: "",
        plays: 0,
        bpm: "",
        key: "",
        explicit: false,
        featured: false,
      },
    ]);
  };

  const uploadAudio = async (i, file) => {
    if (!file) return;
    setError("");
    setUploading(`audio-${i}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTracks((ts) => {
        const next = ts.slice();
        next[i] = {
          ...next[i],
          audio_url: data.url,
          filename: data.filename,
        };
        return next;
      });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  const updateTrack = (i, field, v) => {
    setTracks((ts) => {
      const next = ts.slice();
      next[i] = { ...next[i], [field]: v };
      return next;
    });
  };

  const removeTrack = (i) => {
    setTracks((ts) => ts.filter((_, j) => j !== i));
  };

  const moveTrack = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tracks.length) return;
    const next = tracks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setTracks(next);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        tracks: tracks.map((t) => ({
          id: t.id,
          title: t.title,
          audio_url: t.audio_url,
          duration: t.duration,
          plays: Number(t.plays) || 0,
          bpm: t.bpm ? Number(t.bpm) : null,
          key: t.key || "",
          explicit: !!t.explicit,
          featured: !!t.featured,
        })),
        music_release: release.title || tracks.length > 0 ? release : null,
      };
      const { data } = await api.patch("/users/me", payload);
      onSaved?.(data.user);
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="music-manager-drawer"
      className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="h-full w-full max-w-2xl bg-[#050505] border-l border-[#27272A] overflow-auto">
        <header className="sticky top-0 bg-[#050505] border-b border-[#27272A] p-5 flex items-center justify-between z-10">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
              // GESTOR DE MÚSICA
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
              Mi release
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
          >
            <X size={14} />
          </button>
        </header>

        <div className="p-5 space-y-6">
          {/* Release meta */}
          <div className="grid grid-cols-[auto_1fr] gap-4">
            <div className="relative aspect-square w-28 bg-[#121212] border border-[#27272A] overflow-hidden group">
              {release.cover ? (
                <img
                  src={mediaUrl(release.cover)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[#525252]">
                  <MusicNote size={28} />
                </div>
              )}
              <label className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/60 flex items-center justify-center font-mono text-[10px] tracking-[0.22em] uppercase cursor-pointer transition-opacity">
                {uploading === "cover" ? "Subiendo…" : "Cambiar"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadCover(e.target.files?.[0])}
                  className="hidden"
                />
              </label>
            </div>
            <div className="space-y-3">
              <Field
                label="// TÍTULO"
                value={release.title}
                onChange={(v) => setRelease((r) => ({ ...r, title: v }))}
                placeholder="Ej. Lunar"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>// TIPO</Label>
                  <select
                    value={release.type}
                    onChange={(e) =>
                      setRelease((r) => ({ ...r, type: e.target.value }))
                    }
                    className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2 font-mono text-sm uppercase"
                  >
                    <option>EP</option>
                    <option>ALBUM</option>
                    <option>SINGLE</option>
                    <option>MIXTAPE</option>
                  </select>
                </div>
                <Field
                  label="// AÑO"
                  value={release.year}
                  onChange={(v) => setRelease((r) => ({ ...r, year: v }))}
                />
              </div>
            </div>
          </div>

          {/* Tracks */}
          <div>
            <div className="flex items-center justify-between">
              <Label>// TRACKS ({tracks.length})</Label>
              <button
                data-testid="music-add-track"
                onClick={addTrack}
                className="font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white"
              >
                + Añadir track
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {tracks.length === 0 ? (
                <div className="border-2 border-dashed border-[#27272A] py-10 text-center font-mono text-[10px] text-[#525252] uppercase tracking-[0.22em]">
                  · sin tracks · añade el primero ·
                </div>
              ) : (
                tracks.map((t, i) => (
                  <div
                    key={t.id}
                    data-testid={`track-card-${i}`}
                    className="border border-[#27272A] bg-[#0a0a0a] p-3 hover:border-[#FF3E00] transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em] shrink-0 w-6">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          data-testid={`track-title-${i}`}
                          value={t.title}
                          onChange={(e) => updateTrack(i, "title", e.target.value)}
                          placeholder="Título del track"
                          className="w-full bg-[#121212] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-2 py-1.5 font-display text-[14px]"
                        />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <TinyInput
                            placeholder="BPM"
                            value={t.bpm}
                            onChange={(v) => updateTrack(i, "bpm", v)}
                          />
                          <TinyInput
                            placeholder="Key (Am, Cm…)"
                            value={t.key}
                            onChange={(v) => updateTrack(i, "key", v)}
                          />
                          <TinyInput
                            placeholder="Duración (3:42)"
                            value={t.duration}
                            onChange={(v) => updateTrack(i, "duration", v)}
                          />
                          <TinyInput
                            placeholder="Plays"
                            value={t.plays}
                            onChange={(v) => updateTrack(i, "plays", v)}
                          />
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="inline-flex items-center gap-1 font-mono text-[10px] text-[#A1A1AA] uppercase tracking-[0.22em] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!t.explicit}
                              onChange={(e) =>
                                updateTrack(i, "explicit", e.target.checked)
                              }
                              className="accent-[#FF3E00]"
                            />
                            Explicit
                          </label>
                          <label className="inline-flex items-center gap-1 font-mono text-[10px] text-[#A1A1AA] uppercase tracking-[0.22em] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!t.featured}
                              onChange={(e) =>
                                updateTrack(i, "featured", e.target.checked)
                              }
                              className="accent-[#FF3E00]"
                            />
                            Featured
                          </label>
                          <label
                            data-testid={`track-audio-upload-${i}`}
                            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] cursor-pointer border border-[#27272A] hover:border-[#FF3E00] px-2 py-1"
                          >
                            <UploadSimple size={11} />
                            {uploading === `audio-${i}`
                              ? "Subiendo…"
                              : t.audio_url
                              ? "Reemplazar audio"
                              : "Subir audio"}
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={(e) =>
                                uploadAudio(i, e.target.files?.[0])
                              }
                              className="hidden"
                            />
                          </label>
                          {t.audio_url ? (
                            <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#00FF66] uppercase tracking-[0.22em]">
                              <CheckCircle size={11} weight="fill" /> Audio subido
                            </span>
                          ) : null}
                        </div>
                        {t.audio_url ? (
                          <audio
                            src={mediaUrl(t.audio_url)}
                            controls
                            className="w-full h-8 mt-1"
                          />
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <ActionIcon icon={ArrowUp} onClick={() => moveTrack(i, -1)} />
                        <ActionIcon icon={ArrowDown} onClick={() => moveTrack(i, 1)} />
                        <ActionIcon
                          icon={Trash}
                          danger
                          onClick={() => removeTrack(i)}
                          testid={`track-remove-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {error ? (
            <div className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899]">
              {error}
            </div>
          ) : null}

          <div className="pt-4 border-t border-[#27272A] flex items-center gap-3">
            <button
              data-testid="music-save"
              onClick={save}
              disabled={saving}
              className={`btn-brutal inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-3 ${
                saving
                  ? "bg-[#121212] text-[#525252]"
                  : "bg-[#FF3E00] text-white"
              }`}
            >
              <FloppyDisk size={14} weight="fill" />
              {saving ? "Guardando…" : "Guardar release"}
            </button>
            <button
              onClick={onClose}
              className="font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 text-[#A1A1AA] hover:text-white"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2 font-display text-[14px]"
      />
    </div>
  );
}

function TinyInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-[#121212] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-2 py-1.5 font-mono text-[11px] text-[#A1A1AA]"
    />
  );
}

function ActionIcon({ icon: Icon, onClick, danger, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`h-7 w-7 border inline-flex items-center justify-center transition-colors ${
        danger
          ? "border-[#27272A] text-[#A1A1AA] hover:border-[#FF0033] hover:text-[#FF0033]"
          : "border-[#27272A] text-[#A1A1AA] hover:border-[#FF3E00] hover:text-[#FF3E00]"
      }`}
    >
      <Icon size={11} />
    </button>
  );
}
